// ModernPanelVisibilityMenu — Modern ヘッダ帯の「パネル表示」メニュー (= ペイントツールの
// Window > パネル メニュー相当).
//
// 背景 (docs/_wip/modern-layout.md §9.10 第3スライス、consult 2026-06-14 決定2):
//   - preset を出発点に、ユーザーが各パネル (ツール帯/コマンド/プロパティ/カラー/パレット/
//     プレビュー/レイヤー/社交) を **チェックボックスで表示/非表示** できる手動編集 UI。
//     書き込み先は WorkspaceState.hidden。手動編集が入ると live state は「カスタム」になる。
//   - **discoverable な復帰経路**: 「全部隠して戻せない」を防ぐため、復帰は必ずこのメニュー
//     で可能 (= 各パネルヘッダの × だけに頼らない)。canvas は hidden 対象外 (常に表示) なので
//     リストに出さない (toggle 不可)。
//   - 置き場所は配置ピッカー (ModernWorkspacePresetPicker) 近傍 = 共有 RoomHeader の custom
//     slot。「配置」(preset) の隣に「パネル」(表示トグル) を並べる。
//
// 実装: ボタン + ポップオーバー (checkbox リスト)。outside-click / Escape で閉じる。
// 状態 (どのパネルが表示か) は props (visible map) で受け、トグルは onToggle で親へ流す
// (= state は ModernLayout の live workspace が単一の真実)。
//
// ## popover を document.body へ portal する理由 (2026-06-17 修正、§9.7 z 階層)
// §9.7 の「計測フローティング・スロット方式」では CanvasStage が RoomLayout 直下に
// `position:absolute; z-index:1` で canvas スロットに重なる。ヘッダ chrome は z 指定の
// 無い `relative` なので、ヘッダから **下方向に開く** popover が canvas 矩形へ下りると
// CanvasStage レイヤー (お絵描き canvas) に occlude されて見えない/操作できなかった。
// popover の祖先には stacking context トリガが無いため z-50 を上げても効かない (= 別
// stacking root の絶対配置 stage に負ける)。そこで popover を `createPortal` で
// document.body 直下へ出し、アンカー (「パネル」ボタン) の getBoundingClientRect から
// `position:fixed` 座標を計算して直下に配置する。body 直下 + fixed なら stage の絶対
// レイヤーより確実に前面に出る (= 同一 stacking ルートで z 指定が勝つ)。
// FeedbackButton も同様に fixed で canvas より前面に出ている (右下なので canvas 矩形外
// に開くが、原理は同じ)。
// 位置はメニューを開くたび + 開いている間の resize/scroll で再計算してアンカー追従する。

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import type { PanelId } from './workspaceLayout.js';

/** メニューに出すトグル可能パネル 1 件 (canvas は含めない)。 */
export interface PanelToggleItem {
  readonly id: PanelId;
  /** 表示名 (= aside aria ラベルと同語彙)。 */
  readonly label: string;
  /** 現在表示中か。 */
  readonly visible: boolean;
}

export interface ModernPanelVisibilityMenuProps {
  /** トグル可能パネル一覧 (= canvas を除く全パネル、宣言順 = メニュー表示順)。 */
  readonly items: ReadonlyArray<PanelToggleItem>;
  /** パネルの表示/非表示トグル (= 親が live workspace の hidden を更新)。 */
  readonly onToggle: (id: PanelId, visible: boolean) => void;
}

/**
 * 「パネル」ボタン + チェックボックス ポップオーバー。配置ピッカーの隣に置く。
 * 復帰経路を必ず提供する (= 全部 off でもここで戻せる)。
 */
export const ModernPanelVisibilityMenu = ({
  items,
  onToggle,
}: ModernPanelVisibilityMenuProps): React.ReactNode => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  // portal popover の fixed 配置座標 (= アンカー直下)。null のうちは未計測。
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  // アンカー (ボタン) の getBoundingClientRect から popover の fixed 座標を計算する。
  // ヘッダボタン直下 (= mt-1 相当の 4px gap) に左端を合わせて出す。開くたび + 開いて
  // いる間の resize/scroll で呼び、アンカー追従させる。
  const recomputePos = useCallback((): void => {
    const anchor = anchorRef.current;
    if (anchor === null) return;
    const r = anchor.getBoundingClientRect();
    setPos({ left: r.left, top: r.bottom + 4 });
  }, []);

  // 開いた瞬間 (= mount 前) に座標を確定して初回フレームで正しい位置に出す。
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    recomputePos();
  }, [open, recomputePos]);

  // outside-click / Escape で閉じる (= FeedbackButton 等と同流儀の self-contained popover)。
  // popover は body へ portal するので rootRef.contains では拾えない → menuRef も判定に含める。
  // 開いている間の resize/scroll では座標を再計算してアンカーに追従する (capture phase で
  // 内側スクロール由来の scroll も拾う)。
  useEffect(() => {
    if (!open) return;
    const isInside = (target: Node): boolean =>
      (rootRef.current?.contains(target) ?? false) || (menuRef.current?.contains(target) ?? false);
    const onPointerDown = (e: PointerEvent): void => {
      if (!isInside(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onReflow = (): void => recomputePos();
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [open, recomputePos]);

  const hiddenCount = items.filter((it) => !it.visible).length;

  return (
    <div ref={rootRef} className="relative flex shrink-0 items-center">
      <button
        ref={anchorRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        title="表示するパネルを選ぶ (隠したパネルもここで戻せます)"
        data-manual="layout.panel-menu"
        onClick={() => setOpen((v) => !v)}
        className="shrink-0 cursor-pointer rounded border border-[var(--color-border)] bg-[var(--color-bg-soft)] px-2 py-0.5 text-[11px] font-bold text-white hover:bg-[var(--color-bg)] focus:outline-none focus:ring-1 focus:ring-[var(--color-accent)]"
      >
        パネル{hiddenCount > 0 ? ` (${hiddenCount}件 非表示)` : ''}
      </button>
      {/* popover は body へ portal して CanvasStage (z:1 absolute レイヤー) より前面に
          出す (= ヘッダから下りても canvas に occlude されない、§9.7)。座標はアンカーの
          getBoundingClientRect から fixed で。pos 未計測のうちは描かない。 */}
      {open &&
        pos !== null &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-label="表示するパネル"
            style={{ position: 'fixed', left: pos.left, top: pos.top, zIndex: 60 }}
            className="flex min-w-[180px] flex-col gap-0.5 rounded border border-[var(--color-border)] bg-[var(--color-bg-soft)] p-1 shadow-lg"
          >
            {items.map((it) => (
              <label
                key={it.id}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-[12px] text-white hover:bg-[var(--color-bg)]"
              >
                <input
                  type="checkbox"
                  checked={it.visible}
                  onChange={(e) => onToggle(it.id, e.target.checked)}
                  className="cursor-pointer accent-[var(--color-accent)]"
                />
                <span className="select-none whitespace-nowrap">{it.label}</span>
              </label>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
};
